import * as jsts from 'jsts';

import { AlignmentRules } from '../alignmentRules';
import { Bounding } from '../../../core/models/bounding';
import { BoxElement } from './boxElement';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentBase } from '../equipment-base';
import { EquipmentElements } from '../../../equipment/models/elements/equipmentElements';
import { Extension, ExtensionType, RoundingParams } from '../../../equipment/models/elements/extension';
import { Figure } from '../../../equipment/models/figures/figure';
import { PlaneElement } from './planeElement';
import { Point } from '../../../core/models/point';
import { ProductErrors, ProductGroup } from '../product-group';
import { Rectangle } from '../../../equipment/models/figures/rectangle';
import { RectangleSliced } from '../../../equipment/models/figures/rectangle-sliced';
import { Trapeze } from '../../../equipment/models/figures/trapeze';

import { Utils } from '../../../core/services/utils.service';
import { GlobalSettings } from '../../../core/models/globalSettings';

const DISTANCE_EPSILON: number = .01,
	GEOMETRY_FACTORY: jsts.geom.GeometryFactory = new jsts.geom.GeometryFactory(),
	HEIGHT_EPSILON: number = .01,
	NOT_SHELF_TYPES: Array<EquipmentElements> = [
		EquipmentElements.Showcase,
		EquipmentElements.BackWall,
		EquipmentElements.HookWall,
		EquipmentElements.HookPanel,
		EquipmentElements.PerforatedWall,
		EquipmentElements.Perforation,
		EquipmentElements.SideWalls,
		EquipmentElements.Partition
	],
	ON_SHELF_TYPES: Array<EquipmentElements> = [
		EquipmentElements.Box,
		EquipmentElements.SlicedBox,
		EquipmentElements.Partition,
		EquipmentElements.SideWalls
	],
	THROUGH_SHELF_TYPES: Array<EquipmentElements> = [EquipmentElements.Partition, EquipmentElements.SideWalls],
	BOX_TYPES: Array<EquipmentElements> = [EquipmentElements.Box, EquipmentElements.SlicedBox],
	WALL_TYPES: Array<EquipmentElements> = [
		EquipmentElements.BackWall,
		EquipmentElements.HookWall,
		EquipmentElements.PerforatedWall
	];

export class ShelfElement extends PlaneElement {
	placementHeight: number;
	siblings: Array<ShelfElement>;

	get canHaveSiblings(): boolean {
		let notRounded: boolean = true;

		if (this.hasExtension(ExtensionType.Rounding)) {
			const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);

			notRounded = !rounding.active || (rounding.active && (!rounding.params.leftSideRadius || !rounding.params.rightSideRadius));
		}

		const canHaveSiblings = [
			this.shapes[0].type != 'trapeze',
			notRounded,
			// return this lines if it needs to separate showcases
			// !this.depend || this.depend?.parent.type != 'Showcase',
			// !this.stand.availableElements.some((el: Element) => el.parent.type == 'Showcase' && el.depend == this)
		];

		return canHaveSiblings.every((condition: boolean) => condition);
	}

	get getElementsOnShelf(): Array<Element> {
		return this.stand.allElements
			.filter((el: Element) => {
				let onShelf: boolean = el.depend == this && el.parent.is(...ON_SHELF_TYPES);

				if (!onShelf && el.parent.is(...THROUGH_SHELF_TYPES)) {
					const maxYBounding: number = this.getBoundingY().max,
						intersectsLeftSide: boolean = this.shapes[0].isPointOn(el.getBoundingX().min),
						intersectsRightSide: boolean = this.shapes[0].isPointOn(el.getBoundingX().max),
						intersectsVertical: boolean = el.shapes.some((shape: Figure) => {
							return shape.isPointOn(null, maxYBounding) && maxYBounding < shape.getBoundingY().max;
						});

					onShelf = (intersectsLeftSide || intersectsRightSide) && intersectsVertical;
				}

				return onShelf;
			})
			.sort((a: Element, b: Element) => a.shapes[0].begin.x - b.shapes[0].begin.x);
	}

	getDepthObstacleElements(): Array<Element> {
		return this.getElementsOnShelf;
	}

	get getSiblingElements(): Array<ShelfElement> {
		if (this.siblings) return this.siblings;
		let siblings: Array<ShelfElement> = [];

		if (!this.canHaveSiblings) return [];

		const possibleSiblings: Array<ShelfElement> = this.stand.availableElements
			.filter((el: Element) => el.constructor.name == this.constructor.name && el.canHaveSiblings)
			.filter((el: Element) => {
				const maxDiff: number = Math.abs(el.getBoundingY().max - this.getBoundingY().max),
					minDiff: number = Math.abs(el.getBoundingY().min - this.getBoundingY().min),
					angleShelfCondition: boolean = maxDiff <= HEIGHT_EPSILON && el.parent.is(EquipmentElements.AngleShelf);

				return el != this && ((maxDiff <= HEIGHT_EPSILON && minDiff <= HEIGHT_EPSILON) || angleShelfCondition);
			}) as Array<ShelfElement>;

		if (possibleSiblings.length) {
			[this, ...possibleSiblings]
				.sort((a: ShelfElement, b: ShelfElement) => {
					return a.shapes[0].getBoundingX().min - b.shapes[0].getBoundingX().min;
				})
				.forEach((element: ShelfElement) => {
					if (!siblings?.length) {
						siblings = [element];
						return;
					}

					const prevElement: ShelfElement = siblings[siblings.length - 1];
					const diff: number = Math.abs(element.getBoundingX().min - prevElement.getBoundingX().max);
					const elementOnEdges: Boolean = element.isElementOnShelfStart || prevElement.isElementOnShelfEnd;
					const elementRounding: Extension<RoundingParams> = element.getExtension<RoundingParams>(ExtensionType.Rounding);
					const rounded: boolean = elementRounding?.active && !!elementRounding.params.leftSideRadius;
					const prevElementRounding: Extension<RoundingParams> = prevElement.getExtension<RoundingParams>(ExtensionType.Rounding);
					const prevRounded: boolean = prevElementRounding?.active && !!prevElementRounding.params.rightSideRadius;

					if (diff <= DISTANCE_EPSILON && !elementOnEdges && !rounded && !prevRounded) {
						siblings.push(element);
					} else {
						if (!siblings.some((el: ShelfElement) => el == this)) siblings = [element];
					}
				});
			siblings = siblings.filter((el: ShelfElement) => el != this);
		} else {
			siblings = [];
		}

		return siblings;
	}

	get getSiblings(): Array<ShelfElement> {
		const yBoundingElement: Bounding = this.getBoundingY();
		return this.stand.availableElements
			.filter((el: Element) => el.constructor.name == this.constructor.name)
			.filter((el: ShelfElement) => {
				const maxDiff: number = Math.abs(el.getBoundingY().max - yBoundingElement.max),
					minDiff: number = Math.abs(el.getBoundingY().min - yBoundingElement.min),
					angleShelfCondition: boolean = maxDiff < HEIGHT_EPSILON && el.parent.is(EquipmentElements.AngleShelf);

				return el != this && ((maxDiff < HEIGHT_EPSILON && minDiff < HEIGHT_EPSILON) || angleShelfCondition);
			})
			.sort((a: ShelfElement, b: ShelfElement) => {
				return a.shapes[0].getBoundingX().min - b.shapes[0].getBoundingX().min;
			}) as Array<ShelfElement>;
	}

	get isElementOnShelfStart(): boolean {
		return this.getElementsOnShelf.some((element: Element) => {
			return element.shapes.some((shape: Figure) => shape.getBoundingX().min == this.getBoundingX().min);
		});
	}

	get isElementOnShelfEnd(): boolean {
		return this.getElementsOnShelf.some((element: Element) => {
			return element.shapes.some((shape: Figure) => shape.getBoundingX().max == this.getBoundingX().max);
		});
	}

	get layoutBoundingX(): Bounding {
		const bounding: Bounding = this.getBoundingX();
		const dependentElements: Array<Element> = this.getElementsOnShelf;
		const shapeOnStart: Figure = dependentElements
			.map((element: Element) => element.shapes)
			.flat()
			.find((shape: Figure) => shape.getBoundingX().min == bounding.min);
		const shapeOnEnd: Figure = dependentElements
			.map((element: Element) => element.shapes)
			.flat()
			.find((shape: Figure) => shape.getBoundingX().max == bounding.max);

		return {
			min: Utils.round(bounding.min + (shapeOnStart ? shapeOnStart.width : 0)),
			max: Utils.round(bounding.max - (shapeOnEnd ? shapeOnEnd.width : 0)),
		}
	}

	get layoutBoundingZ(): Bounding {
		const bounding: Bounding = this.getBoundingZ();
		const dependentElement: Element = this.stand.allElements
			.filter((element: Element) => element.depend == this)
			.find((element: Element) => element.parent.is(...WALL_TYPES))

		return {
			min: Utils.round(bounding.min + (dependentElement ? dependentElement.Depth : 0)),
			max: bounding.max,
		}
	}

	get linearMeters(): number {
		if (this.isExtensionActive(ExtensionType.Rounding)) {
			const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);
			const leftSideRadius: number = Utils.convertPercentToRadius(rounding.params.leftSideRadius, this);
			const rightSideRadius: number = Utils.convertPercentToRadius(rounding.params.rightSideRadius, this);
			const radiusSum: number = Utils.round(leftSideRadius + rightSideRadius);
			const width: number = Utils.round(this.Width - radiusSum + Math.PI * radiusSum / 2);

			return this.twoSided ? width * 2 : width;
		}

		return this.twoSided ? this.Width * 2 : this.Width;
	}

	constructor(element: Element) {
		super(element);
	}

	canDrop(product: ProductGroup): boolean {
		return !product.isLooseForm;
	}

	copy(): ShelfElement {
		const shapes: Array<Figure> = this.shapes.map((shape: Figure) => shape.copy()),
			extensions: Array<Extension<any>> = this.extensions.map((extension: Extension<any>) => extension.copy()),
			element: Element = new Element(this.parent, shapes, extensions, this.depend, this.id, this.idx);

		return new ShelfElement(element);
	}

	// TODO divide into front and back
	getDroppedProduct(x: number, y: number, z: number, activeProducts: Array<ProductGroup> = [], product?: ProductGroup): ProductGroup {
		let products: Array<ProductGroup> = [this, ...this.getSiblingElements]
			.reduce((products: Array<Array<ProductGroup>>, el: ShelfElement) => [...products, ...el.products], [])
			.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
				if (this.twoSided && z < this.center.z) {
					return a[0].begin.z - b[0].begin.z;
				}
				return b[0].begin.z - a[0].begin.z;
			})
			.find((group: Array<ProductGroup>) => {
				return Element.calculateGroupMin(group) <= x && Element.calculateGroupMax(group) > x;
			});

		if (!products) return null;

		if (this.twoSided) {
			const center: Point = this.center
			if (z < center.z) {
				products = products.filter((pg: ProductGroup) => pg.begin.z < center.z);
			} else {
				products = products.filter((pg: ProductGroup) => pg.begin.z >= center.z);
			}
		}

		if (!products.length) return null;

		let zProducts: Array<ProductGroup> = Utils.groupByChildProperty(products, 'begin', 'z')
				.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
					if (this.twoSided && z < this.center.z) {
						return a[0].begin.z - b[0].begin.z;
					}
					return b[0].begin.z - a[0].begin.z;
				})[0],
			existDepend: Array<ProductGroup> = zProducts
				.filter((pg: ProductGroup) => pg.dependProduct)
				.map((pg: ProductGroup) => pg.dependProduct);

		if (product) {
			const topActiveProducts: Array<ProductGroup> = activeProducts.filter((pg: ProductGroup) => {
				return pg.getBoundingY().max >= product.getBoundingY().max;
			});

			zProducts = zProducts.filter((pg: ProductGroup) => pg !== product && !topActiveProducts.includes(pg));

			if (product.dependProduct) {
				existDepend = existDepend.filter((pg: ProductGroup) => pg != product.dependProduct);
			}
		}

		zProducts = zProducts.filter((pg: ProductGroup) => {
			return pg.getBoundingY().max < y && !existDepend.includes(pg);
		});

		return zProducts.length ? zProducts[0] : null;
	}

	getPointedElement(x: number, y: number, stand: EquipmentBase, returnNull: boolean = false): Element {
		const elements: Array<Element> = stand.allElements
			.filter((el: Element) => el.depend == this && !el.parent.is(...NOT_SHELF_TYPES));

		for (let el of elements) {
			for (let shape of el.shapes) {
				if (shape.isPointOn(x, null, y)) return el;
			}
		}

		return returnNull ? null : this;
	}

	private getSegmentsBy(...types: Array<EquipmentElements>): Array<Bounding> {
		const boundingY: Bounding = this.getBoundingY();
		const boundingX: Bounding = this.getBoundingX();
		const elementsOnShelves: Array<Element> = [this, ...this.getSiblingElements]
			.reduce((result: Array<Element>, el: ShelfElement) => [...result, ...el.getElementsOnShelf], [])
			.filter((el: Element) => types.includes(el.parent.type));

		return Utils.to1DArray(elementsOnShelves.map((el: Element) => el.shapes))
			.filter((shape: Figure) => {
				if (!shape.isPointOn(null, boundingY.min) && !shape.isPointOn(null, boundingY.max)) return false;
				const shapeBoundingX: Bounding = shape.getBoundingX();
				return shapeBoundingX.min <= boundingX.max && shapeBoundingX.max >= boundingX.min;
			})
			.map((shape: Figure) => shape.getBoundingX())
			.sort((a: Bounding, b: Bounding) => a.min - b.min);
	}

	getSiblingSegments(siblingsPoint: boolean = true, includeBoxes: boolean = true): Array<Bounding> {
		const siblings: Array<ShelfElement> = this.getSiblingElements,
			pointsArray: Array<ShelfElement> = siblingsPoint ? [this, ...siblings] : [this],
			minSiblingPoint: number = Utils.min(
				pointsArray.map((el: ShelfElement) => el.getBoundingX(this.stand.alignment !== 'default').min)
			),
			maxSiblingPoint: number = Utils.max(
				pointsArray.map((el: ShelfElement) => el.getBoundingX(this.stand.alignment !== 'default').max)
			);

		let segments: Array<Bounding>,
			boxesSegments: Array<Bounding> = [],
			elementsOnShelves: Array<Element> = [this, ...siblings]
				.reduce((result: Array<Element>, el: ShelfElement) => [...result, ...el.getElementsOnShelf], []),
			boxesOnShelves: Array<BoxElement> = elementsOnShelves
				.filter((el: Element) => el.parent.is(...BOX_TYPES)) as Array<BoxElement>,
			boxesMatrix: Array<boolean> = [];

		boxesOnShelves.forEach((box: BoxElement) => {
			const bounding: Bounding = box.getBoundingX();
			for (let i = bounding.min * Math.pow(10, 3); i <= bounding.max * Math.pow(10, 3); i++) {
				boxesMatrix[i] = true;
			}
		});

		elementsOnShelves = elementsOnShelves
			.filter((el: Element) => el.parent.is(...THROUGH_SHELF_TYPES));

		if (elementsOnShelves.length) {
			const shapesBounding: Array<Bounding> = Utils
				.to1DArray(elementsOnShelves.map((el: Element) => el.shapes))
				.map((shape: Figure) => shape.getBoundingX())
				.sort((a: Bounding, b: Bounding) => a.min - b.min);

			segments = shapesBounding.map((bounding: Bounding, idx: number) => {
				return {
					min: Math.max(minSiblingPoint, shapesBounding[idx - 1]?.max ?? 0),
					max: Math.min(maxSiblingPoint, bounding.min)
				};
			});
			segments.push({ min: shapesBounding[shapesBounding.length - 1].max, max: maxSiblingPoint });
			segments = segments.filter((bounding: Bounding) => Utils.round(bounding.max - bounding.min) > 0);
		} else {
			segments = [{ min: minSiblingPoint, max: maxSiblingPoint }];
		}

		let boxesDividePoints: Array<number> = boxesMatrix
			.map((value: boolean, index: number) => {
				return !boxesMatrix[index - 1] || !boxesMatrix[index + 1] ? index / Math.pow(10, 3) : null;
			})
			.filter((value: number) => value != null);

		segments.forEach((limit: Bounding) => {
			let prevPoint: number = limit.min;
			boxesDividePoints.forEach((point: number) => {
				if (prevPoint < point && point < limit.max) {
					boxesSegments.push({ min: prevPoint, max: point });
					prevPoint = point;
				}
			});
			if (prevPoint < limit.max) {
				boxesSegments.push({ min: prevPoint, max: limit.max });
			}
		});

		if (!includeBoxes) {
			const maxPoints: Array<number> = boxesDividePoints
					.filter((point: number, index: number) => index % 1 === 0 && index % 2 !== 0),
				minPoints: Array<number> = boxesDividePoints
					.filter((point: number, index: number) => index % 1 !== 0 || index % 2 === 0),
				boxesPoints: Array<Bounding> = minPoints.map((point: number, index: number) => {
					return { min: point, max: maxPoints[index] };
				});

			boxesSegments = boxesSegments.filter((segment: Bounding) => {
				return !boxesPoints.some((points: Bounding) => points.min == segment.min && points.max == segment.max);
			});
		}

		return boxesSegments;
	}

	hasBoxIntersections(product: ProductGroup): boolean {
		const siblings: Array<ShelfElement> = this.getSiblingElements,
			shelfBoxes: Array<BoxElement> = this.stand.allElements.filter((element: Element) => {
				return [this, ...siblings].some((el: Element) => {
					return element.parent.is(...BOX_TYPES) && element.depend == el;
				});
			}) as Array<BoxElement>;

		return shelfBoxes.length && shelfBoxes.some((box: BoxElement) => {
			return box.zPolygon.intersects(product.zPolygon) && !box.zPolygon.touches(product.zPolygon)
		});
	}

    hasCentralPillarIntersections(product: ProductGroup): boolean {
		const siblings: Array<ShelfElement> = this.getSiblingElements,
			centralPillars: Array<Element> = this.stand.allElements.filter((element: Element) => {
				return [this, ...siblings].some((el: Element) => {
					const isCentralPillar: boolean = element.parent.is(EquipmentElements.CentralPillar);
					const isRelated: boolean = element.depend == el || el.depend == element;
					const intersects: boolean = element.getBoundingY().max > el.getBoundingY().max;

					return isCentralPillar && isRelated && intersects;
				});
			});

		return centralPillars.length && centralPillars.some((partition: Element) => {
            const zPolygon = GEOMETRY_FACTORY.createPolygon(partition.zCoordinates.map(i => new jsts.geom.Coordinate(i.x, i.y)));
			return zPolygon.intersects(product.zPolygon) && !zPolygon.touches(product.zPolygon)
		});
	}

    hasPartitionIntersections(product: ProductGroup): boolean {
		const siblings: Array<ShelfElement> = this.getSiblingElements,
			shelfPartitions: Array<Element> = this.stand.allElements.filter((element: Element) => {
				return [this, ...siblings].some((el: Element) => {
					return element.parent.is(EquipmentElements.Partition) && element.depend == el;
				});
			});

		return shelfPartitions.length && shelfPartitions.some((partition: Element) => {
            const zPolygon = GEOMETRY_FACTORY.createPolygon(partition.zCoordinates.map(i => new jsts.geom.Coordinate(i.x, i.y)));
			return zPolygon.intersects(product.zPolygon) && !zPolygon.touches(product.zPolygon)
		});
	}

	hasShelfIntersections(product: ProductGroup, beginX: number = product.getBoundingX().min): boolean {
		if (product.error) return true;

		// 1. Знайти всі полиці на стелажі, які знаходяться над продуктом;
		// 2. Відсортувати їх від ближньої до дальньої;
		// 3. Знайти полицю, яка знаходиться безпосередньо над продуктом;
		const topShelf: ShelfElement = this.stand.availableElements
			.filter((el: Element) => {
				return el != this &&
					el.constructor.name == this.constructor.name &&
					el.getBoundingY().max - HEIGHT_EPSILON > this.getBoundingY().max;
			})
			.sort((a: ShelfElement, b: ShelfElement) => a.shapes[0].begin.y - b.shapes[0].begin.y)
			.find((shelf: ShelfElement) => {
				const shelfBounding: Bounding = shelf.getBoundingX(),
					endX: number = Utils.round(beginX + product.Width),
					filter: Array<boolean> = [
						shelfBounding.min < beginX && beginX < shelfBounding.max,
						shelfBounding.min < endX && endX < shelfBounding.max,
						beginX <= shelfBounding.min && shelfBounding.max <= endX
					];
				return filter.some((condition: boolean) => condition && product.getBoundingZ().min < shelf.getBoundingZ().max);
			}) as ShelfElement;
		if (!topShelf) return false;

		const shelfPoints: Array<Point> = [
			{
				x: topShelf.shapes[0].begin.x,
				y: topShelf.shapes[0].begin.y,
				z: Utils.round(topShelf.shapes[0].begin.z + topShelf.shapes[0].depth)
			},
			{
				x: topShelf.shapes[0].begin.x,
				y: Utils.round(topShelf.shapes[0].begin.y + topShelf.shapes[0].height),
				z: Utils.round(topShelf.shapes[0].begin.z + topShelf.shapes[0].depth)
			},
			{
				x: topShelf.shapes[0].begin.x,
				y: Utils.round(topShelf.shapes[0].begin.y + topShelf.shapes[0].height),
				z: topShelf.shapes[0].begin.z
			},
			{
				x: topShelf.shapes[0].begin.x,
				y: topShelf.shapes[0].begin.y,
				z: topShelf.shapes[0].begin.z
			},
			{
				x: topShelf.shapes[0].begin.x,
				y: topShelf.shapes[0].begin.y,
				z: Utils.round(topShelf.shapes[0].begin.z + topShelf.shapes[0].depth)
			}
		];
		const pgPolygons: Array<jsts.geom.Polygon> = [];
        const productZCoordinates: Array<Point> = product.zCoordinates;
		const productPointsLeftSide: Array<Point> = [
			{ x: productZCoordinates[0].x, y: product.begin.y, z: productZCoordinates[0].y },
			{ x: productZCoordinates[0].x, y: Utils.round(product.begin.y + product.Height), z: productZCoordinates[0].y },
			{ x: productZCoordinates[3].x, y: Utils.round(product.begin.y + product.Height), z: productZCoordinates[3].y },
			{ x: productZCoordinates[3].x, y: product.begin.y, z: productZCoordinates[3].y },
			{ x: productZCoordinates[0].x, y: product.begin.y, z: productZCoordinates[0].y }
		];
		if (productPointsLeftSide.some((point: Point) => point.z !== productPointsLeftSide[0].z)) {
			pgPolygons.push(
				GEOMETRY_FACTORY.createPolygon(
					productPointsLeftSide.map((point: Point) => {
						if (this.parent.is(EquipmentElements.AngleShelf)) {
							const rotated: Point = (this.shapes[0] as Rectangle | RectangleSliced).rotatePointByFrontAngle(point);
							return new jsts.geom.Coordinate(rotated.z, rotated.y, 0);
						} else {
							return new jsts.geom.Coordinate(point.z, point.y, 0);
						}
					})
				)
			);
		}
		const productPointsRightSide: Array<Point> = [
			{ x: productZCoordinates[1].x, y: product.begin.y, z: productZCoordinates[1].y },
			{ x: productZCoordinates[1].x, y: Utils.round(product.begin.y + product.Height), z: productZCoordinates[1].y },
			{ x: productZCoordinates[2].x, y: Utils.round(product.begin.y + product.Height), z: productZCoordinates[2].y },
			{ x: productZCoordinates[2].x, y: product.begin.y, z: productZCoordinates[2].y },
			{ x: productZCoordinates[1].x, y: product.begin.y, z: productZCoordinates[1].y }
		];
		if (productPointsRightSide.some((point: Point) => point.z !== productPointsRightSide[0].z)) {
			pgPolygons.push(
				GEOMETRY_FACTORY.createPolygon(
					productPointsRightSide.map((point: Point) => {
						if (this.parent.is(EquipmentElements.AngleShelf)) {
							const rotated: Point = (this.shapes[0] as Rectangle | RectangleSliced).rotatePointByFrontAngle(point);
							return new jsts.geom.Coordinate(rotated.z, rotated.y, 0);
						} else {
							return new jsts.geom.Coordinate(point.z, point.y, 0);
						}
					})
				)
			);
		}
        const productPointsFrontSide: Array<Point> = [
			{ x: productZCoordinates[0].x, y: product.begin.y, z: productZCoordinates[0].y },
			{ x: productZCoordinates[1].x, y: product.begin.y, z: productZCoordinates[1].y },
			{ x: productZCoordinates[1].x, y: Utils.round(product.begin.y + product.Height), z: productZCoordinates[1].y },
			{ x: productZCoordinates[0].x, y: Utils.round(product.begin.y + product.Height), z: productZCoordinates[0].y },
			{ x: productZCoordinates[0].x, y: product.begin.y, z: productZCoordinates[0].y }
		];
		if (productPointsFrontSide.some((point: Point) => point.z !== productPointsFrontSide[0].z)) {
			pgPolygons.push(
				GEOMETRY_FACTORY.createPolygon(
					productPointsFrontSide.map((point: Point) => {
						if (this.parent.is(EquipmentElements.AngleShelf)) {
							const rotated: Point = (this.shapes[0] as Rectangle | RectangleSliced).rotatePointByFrontAngle(point);
							return new jsts.geom.Coordinate(rotated.z, rotated.y, 0);
						} else {
							return new jsts.geom.Coordinate(point.z, point.y, 0);
						}
					})
				)
			);
		}
		const productPointsBackSide: Array<Point> = [
			{ x: productZCoordinates[2].x, y: product.begin.y, z: productZCoordinates[2].y },
			{ x: productZCoordinates[3].x, y: product.begin.y, z: productZCoordinates[3].y },
			{ x: productZCoordinates[3].x, y: Utils.round(product.begin.y + product.Height), z: productZCoordinates[3].y },
			{ x: productZCoordinates[2].x, y: Utils.round(product.begin.y + product.Height), z: productZCoordinates[2].y },
			{ x: productZCoordinates[2].x, y: product.begin.y, z: productZCoordinates[2].y }
		];
		if (productPointsBackSide.some((point: Point) => point.z !== productPointsBackSide[0].z)) {
			pgPolygons.push(
				GEOMETRY_FACTORY.createPolygon(
					productPointsBackSide.map((point: Point) => {
						if (this.parent.is(EquipmentElements.AngleShelf)) {
							const rotated: Point = (this.shapes[0] as Rectangle | RectangleSliced).rotatePointByFrontAngle(point);
							return new jsts.geom.Coordinate(rotated.z, rotated.y, 0);
						} else {
							return new jsts.geom.Coordinate(point.z, point.y, 0);
						}
					})
				)
			);
		}
		const shelfPoly: jsts.geom.Polygon = GEOMETRY_FACTORY.createPolygon(
			shelfPoints.map((point: Point) => {
				if (topShelf.parent.is(EquipmentElements.AngleShelf)) {
					const rotated: Point = (topShelf.shapes[0] as Rectangle | RectangleSliced).rotatePointByFrontAngle(point);
					return new jsts.geom.Coordinate(rotated.z, rotated.y, 0);
				} else {
					return new jsts.geom.Coordinate(point.z, point.y, 0);
				}
			})
		);

		return pgPolygons.some((pgPoly: jsts.geom.Polygon) => shelfPoly.intersects(pgPoly) && !shelfPoly.touches(pgPoly));
	}

	hasTrapezeIntersections(product: ProductGroup): boolean {
		const shelfPoly = GEOMETRY_FACTORY.createPolygon(
			(this.shapes[0] as Trapeze).coordinatesZ.map((point: Point) => new jsts.geom.Coordinate(point.x, point.y))
		);
		const productPoly = product.zPolygon;

		return !shelfPoly.contains(productPoly);
	}

	normalize(stand: EquipmentBase, segments: Array<Bounding> = []): void {
		try {
			let zGroups: Array<Array<ProductGroup>> = [this, ...this.getSiblingElements]
				.reduce((products: Array<Array<ProductGroup>>, el: ShelfElement) => [...products, ...el.filterArbitraryProducts(el.products)], []);
			const shelfSegments: Array<Bounding> = segments.length
				? segments
				: stand.alignment !== 'default' ? this.getBlockSegments : this.getSiblingSegments();
			const shelfLimits: Bounding = {
				min: Utils.min(shelfSegments.map((seg: Bounding) => seg.min)),
				max: Utils.max(shelfSegments.map((seg: Bounding) => seg.max))
			};

			if (zGroups.length) {
				// sort products and shelf segments according to selected alignment
				if (stand.alignment === 'right') {
					zGroups.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
						return Element.calculateGroupMax(b) - Element.calculateGroupMax(a);
					});
					shelfSegments.sort((a: Bounding, b: Bounding) => b.max - a.max);
				} else {
					zGroups.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => {
						return Element.calculateGroupMin(a) - Element.calculateGroupMin(b);
					});
				}

				// shift left/right products which intersect the partitions by endpoints
				this.getSegmentsBy(EquipmentElements.Partition, EquipmentElements.SideWalls)
					.forEach((segment: Bounding) => {
						zGroups.forEach((group: Array<ProductGroup>) => {
							const groupLimits: Bounding = {
								min: Element.calculateGroupMin(group),
								max: Element.calculateGroupMax(group)
							};

							if (groupLimits.min >= segment.min && groupLimits.min < segment.max) {
								group.forEach((pg: ProductGroup) => {
									const deltaX: number = pg.getBoundingX().min - pg.begin.x;
									pg.begin.x = segment.max - deltaX;
								});
							}

							if (groupLimits.max > segment.min && groupLimits.max <= segment.max) {
								group.forEach((pg: ProductGroup) => {
									const deltaX: number = pg.getBoundingX().max - pg.begin.x;
									pg.begin.x = Utils.round(segment.min - deltaX)
								});
							}
						});
					});

				if (!stand.arbitraryLayout) {
					shelfSegments.forEach((segment: Bounding) => {
						if (this.twoSided) {
							this.normalizeSegment(
								zGroups.filter((g: Array<ProductGroup>) => g[0].begin.z < this.center.z),
								segment,
								shelfLimits
							);
							this.normalizeSegment(
								zGroups.filter((g: Array<ProductGroup>) => g[0].begin.z >= this.center.z),
								segment,
								shelfLimits
							);
						} else {
							this.normalizeSegment(zGroups, segment, shelfLimits);
						}
					});
				}
			}

			// highlight product group when error
			[this, ...this.getSiblingElements].forEach((el: Element) => (el as ShelfElement).setProductErrors(shelfLimits));
		} catch (err) {
			console.error(err);
		}
	}

	private normalizeSegment(zGroups: Array<Array<ProductGroup>>, segment: Bounding, limits: Bounding): void {
		// filter products which belongs to current segment, find previous product on shelves
		const segGroups: Array<Array<ProductGroup>> = zGroups.filter((group: Array<ProductGroup>, idx: number) => {
				const align: AlignmentRules = Element.alignmentRules(zGroups, idx, segment);
				return align[this.stand.alignment].checkAffiliation(limits);
			}),
			firstGroupIndex: number = segGroups.length
				? zGroups.findIndex((group: Array<ProductGroup>) => group == segGroups[0])
				: 0,
			prevProduct: Bounding = firstGroupIndex
				? {
					min: Element.calculateGroupMin(zGroups[firstGroupIndex - 1]),
					max: Element.calculateGroupMax(zGroups[firstGroupIndex - 1])
				}
				: null;

		segGroups.forEach((group: Array<ProductGroup>, idx: number) => {
			const align: AlignmentRules = Element.alignmentRules(segGroups, idx, segment);
			let beginPoint: number = !idx
				? align[this.stand.alignment].startPoint(prevProduct)
				: align[this.stand.alignment].nextPoint();

			if (this.twoSided && group[0].begin.z < this.center.z) {
				beginPoint = Utils.round(beginPoint + Element.calculateGroupWidth(group));
			}

			// set default angle for product group
			group.forEach((pg: ProductGroup) => {
				if (this.twoSided && pg.begin.z < this.center.z) {
					pg.angle = GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE + 180;
				} else {
					pg.angle = GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;
				}
			});

			// set correct x coordinate for product group
			group.forEach((pg: ProductGroup) => {
				pg.begin.x = isFinite(beginPoint) ? beginPoint : align[this.stand.alignment].startPoint();
			});

			// set new depend element for product group
			const xBoundingElement: Bounding = group[0].element.getBoundingX(),
				groupMin: number = Element.calculateGroupMin(group),
				groupMax: number = Element.calculateGroupMax(group);

			if (
				(groupMax > xBoundingElement.max || groupMin < xBoundingElement.min) &&
				group[0].element.constructor.name == this.constructor.name
			) {
				const siblings: Array<Element> = group[0].element.getSiblingElements,
					possibleElement: Element = siblings.find((el: Element) => {
						return el.getBoundingX().max >= align[this.stand.alignment].checkDependingPoint() &&
							el.getBoundingX().min <= align[this.stand.alignment].checkDependingPoint();
					});
				if (possibleElement) group.forEach((pg: ProductGroup) => {
					const saveZ: number = pg.begin.z;
					pg.Element = possibleElement;
					pg.begin.z = saveZ;
				});
			}

			// set correct z coordinate for product group
			group
				.filter((pg: ProductGroup) => !pg.dependProduct)
				.sort((a: ProductGroup, b: ProductGroup) => {
					if (this.twoSided && group[0].begin.z < this.center.z) {
						return a.getBoundingZ().max - b.getBoundingZ().max || -1;
					}
					return b.getBoundingZ().max - a.getBoundingZ().max || -1;
				})
				.forEach((pg: ProductGroup, idx: number, list: Array<ProductGroup>) => {
					pg.begin.y = pg.element.getBoundingY().max;

					let z: number;

					if (this.twoSided && pg.begin.z < this.center.z) {
						z = !idx ? pg.element.getAbsoluteBoundingZ().min : list[idx - 1].getBoundingZ().max;
					} else {
						z = !idx ? pg.element.getAbsoluteBoundingZ().max : list[idx - 1].getBoundingZ().min;
					}

					// розрахунок z товару для трапецій
					if (pg.element.shapes[0].type == 'trapeze' &&
						pg.element.shapes[0].frontWidth < pg.element.shapes[0].backWidth &&
						!idx
					) {
						z = pg.zMax;

						pg.begin.z = Utils.round(z);

						if (pg.getBoundingZ().min <= pg.element.shapes[0].begin.z) {
							z = pg.element.shapes[0].begin.z + pg.Depth;
						}
					}

					pg.begin.z = Utils.round(z);
				});

			group
				.filter((pg: ProductGroup) => pg.dependProduct)
				.sort((a: ProductGroup, b: ProductGroup) => a.begin.y - b.begin.y)
				.forEach((pg: ProductGroup) => {
					pg.begin.y = pg.dependProduct.getBoundingY().max;
					pg.begin.z = pg.dependProduct.begin.z;
				});
		});
	}

	setPlacementHeight(stand: EquipmentBase): void {
		const bases: Array<Element> = stand.bases;

		bases.forEach((base: Element) => {
			const shelves: Array<ShelfElement> = stand.availableElements
					.filter((el: Element) => el.constructor == ShelfElement)
					.filter((el: ShelfElement) => el.getDepends().includes(base) || el == base)
					.sort((a: ShelfElement, b: ShelfElement) => {
						return a.getBoundingY().max - b.getBoundingY().max;
					}) as Array<ShelfElement>,
				commonBegin: number = Utils.common(shelves.map((shelf: ShelfElement) => shelf.getBoundingX().min));

			for (let i = 0; i < shelves.length - 1; i++) {
				const distance: { from: Point, to: Point } = {
					from: {
						x: commonBegin,
						y: this.Height - shelves[i].getBoundingY().max
					},
					to: {
						x: commonBegin,
						y: this.Height - shelves[i + 1].getBoundingY().min
					}
				};

				if (distance.from.y - distance.to.y > 0) {
					(shelves[i] as ShelfElement).placementHeight = Utils
						.distance(distance.from.x, distance.from.y, distance.to.x, distance.to.y);
				}
			}
		});
	}

	protected findPriorProduct(group: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): ProductGroup {
		if (list.length == 1) {
			let bounding: Bounding;

			if (group[0].angle >= 0 && group[0].angle < 180) {
				bounding = { min: point.x, max: Utils.round(point.x + group[0].Width) };
			} else {
				bounding = { min: Utils.round(point.x - group[0].Width), max: point.x };
			}

			if (this.twoSided && point.z < this.center.z) {
				const sameXProducts: Array<Array<ProductGroup>> = this.findSameXProducts(group, bounding).reverse();

				if (sameXProducts.length) {
					return sameXProducts[0]
						.sort((a: ProductGroup, b: ProductGroup) => b.getBoundingZ().max - a.getBoundingZ().max)
						.find((pg: ProductGroup) => pg.getBoundingZ().max < point.z);
				}
			} else {
				const sameXProducts: Array<Array<ProductGroup>> = this.findSameXProducts(group, bounding);

				if (sameXProducts.length) {
					return sameXProducts[0]
						.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingZ().min - b.getBoundingZ().min)
						.find((pg: ProductGroup) => pg.getBoundingZ().min > point.z);
				}
			}
		}

		return null;
	}

	protected adjustPointToPriorProduct(priorPg: ProductGroup, group: Array<ProductGroup>, point: Point): void {
		point.x = priorPg.begin.x;
		point.y = this.getBoundingY().max;
		point.z = priorPg.angle ? priorPg.getBoundingZ().max : priorPg.getBoundingZ().min;
	}

	protected getItemsZCoordinates(productGroups: Array<ProductGroup>, product: ProductGroup): Array<Array<Point>> {
		const productCoordinates: Array<Array<Point>> = super.getItemsZCoordinates(productGroups, product);
		const productBoundingX: Bounding = product.getBoundingX();
		const productBoundingZ: Bounding = product.getBoundingZ();

		const coordinates: Array<Point> = this.zCoordinates;
		const otherElementsCoordinates: Array<Array<Point>> = this.getElementsOnShelf
			.filter((element: Element) => {
				const boundingX: Bounding = element.getBoundingX();
				const boundingZ: Bounding = element.getBoundingZ();
				const isWithinX: boolean = boundingX.max >= productBoundingX.min && boundingX.min <= productBoundingX.max;
				const isWithinZ:boolean = boundingZ.max >= productBoundingZ.min && boundingZ.min <= productBoundingZ.max;
				return isWithinX || isWithinZ;
			})
			.map((element: Element) => element.zCoordinates);

		if (this.twoSided) {
			let centralPillar: Element;

			if (this.parent.is(EquipmentElements.Shelf)) {
				centralPillar = this.depend;
			} else if (this.parent.is(EquipmentElements.BaseRectangle)) {
				centralPillar = this.relatedElements.find((el: Element) => el.parent.is(EquipmentElements.CentralPillar));
			}

			if (centralPillar.getBoundingY().max > this.getBoundingY().max) {
				return [...productCoordinates, coordinates, centralPillar.zCoordinates, ...otherElementsCoordinates]
			}
		}

		return [...productCoordinates, coordinates, ...otherElementsCoordinates];
	}

	protected setProductErrors(limits: Bounding): void {
		this.products.forEach((group: Array<ProductGroup>) => {
			group.forEach((pg: ProductGroup) => {
				pg.error = false;

				const bounding: Bounding = pg.getBoundingX();
				const errors: { [key in ProductErrors]?: boolean } = {
					[ProductErrors.ShelfDepth]: pg.getBoundingZ().min < this.getAbsoluteBoundingZ().min
						|| pg.getBoundingZ().max > this.getAbsoluteBoundingZ().max,
					[ProductErrors.PartitionIntersection]: this.hasPartitionIntersections(pg),
					[ProductErrors.CentralPillarIntersection]: this.hasCentralPillarIntersections(pg),
					[ProductErrors.SideWallsIntersection]: this.getSegmentsBy(EquipmentElements.SideWalls)
						.reduce((points: Array<number>, segment: Bounding) => {
							return [...points, segment.min, segment.max];
						}, [])
						.some((point: number) => bounding.min < point && point < bounding.max),
					[ProductErrors.ShelfLeftLimit]: bounding.min < limits.min,
					[ProductErrors.ShelfRightLimit]: bounding.max > limits.max,
					[ProductErrors.ElementHeight]: this.hasShelfIntersections(pg),
					[ProductErrors.BoxIntersection]: this.hasBoxIntersections(pg),
					[ProductErrors.ProductIntersection]: this.intersectsProduct(pg),
					[ProductErrors.LooseOnShelf]: pg.isLooseForm
				};

				if (pg.dependProduct && !this.stand.arbitraryLayout) {
					errors[ProductErrors.DependLimits] = pg.dependProduct.Width < pg.Width;
					errors[ProductErrors.DependDepth] = pg.dependProduct.Depth < pg.Depth;
				}

				if (pg.element.isExtensionActive(ExtensionType.Rounding)) {
					const rounding: Extension<RoundingParams> = this.getExtension<RoundingParams>(ExtensionType.Rounding);

					if (rounding.params.leftSideRadius) {
						errors[ProductErrors.ShelfLeftLimit] = bounding.min < pg.element.getBoundingX().min
							|| this.hasRoundingIntersections(pg, 'left');
					}

					if (rounding.params.rightSideRadius) {
						errors[ProductErrors.ShelfRightLimit] = bounding.max > pg.element.getBoundingX().max
							|| this.hasRoundingIntersections(pg, 'right');
					}
				}

				if (pg.element.shapes[0].type === 'trapeze') {
					errors[ProductErrors.ShelfLeftLimit] = bounding.min < pg.element.getBoundingX().min;
					errors[ProductErrors.ShelfRightLimit] = bounding.max > pg.element.getBoundingX().max;
					errors[ProductErrors.TrapezeLimits] = this.hasTrapezeIntersections(pg)
						&& !errors[ProductErrors.ShelfDepth]
						&& !errors[ProductErrors.ShelfLeftLimit]
						&& !errors[ProductErrors.ShelfRightLimit];
				}

				pg.error = Object.values(errors).some((error: boolean) => error);
				pg.errorTypes = Object.keys(errors).filter((key: ProductErrors) => errors[key]);
			});
		})
	}

	public setProductPlacementHeight(pg: ProductGroup, placementHeight: number) {
		const height: number = this.stand.Height;
		if (!placementHeight) {
			do {
				pg.placementHeight++;
			}
			while (!this.hasShelfIntersections(pg) && height >= pg.getBoundingY().max);
			pg.placementHeight--;
		} else if (placementHeight > 1) {
			pg.placementHeight = placementHeight;
			if (this.hasShelfIntersections(pg) || (height < pg.getBoundingY().max && pg.placementHeight > 1)) {
				do {
					pg.placementHeight--;
				}
				while (this.hasShelfIntersections(pg) || (height < pg.getBoundingY().max && pg.placementHeight > 1));
			}
		}
	}
}
